import React, { useState, useEffect, useMemo } from 'react';
import { Header } from './components/Header';
import { FilterBar } from './components/FilterBar';
import { AppGrid } from './components/AppGrid';
import { appService, departmentService } from './services/api';
import { App, Department } from './types/app';
import { validateSession, signIn } from './services/auth';
import './App.css';

const AppMain: React.FC = () => {
  const [apps, setApps] = useState<App[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function checkAuth() {
      const valid = await validateSession();
      if (!valid) {
        await signIn(window.location.pathname);
        return;
      }
      setAuthReady(true);
    }
    checkAuth();
  }, []);

  useEffect(() => {
    if (!authReady) return;

    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        const [appsData, departmentsData] = await Promise.all([
          appService.getAll({ isOkta: true }),
          departmentService.getWithAppCount({ isOkta: true }),
        ]);

        setApps(appsData);
        setDepartments(departmentsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load apps');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [authReady]);

  const filteredApps = useMemo(() => {
    let result = apps;

    if (selectedDepartmentId !== null) {
      result = result.filter((app) => app.departmentId === selectedDepartmentId);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (app) =>
          app.name.toLowerCase().includes(query) ||
          app.description.toLowerCase().includes(query) ||
          app.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    return result;
  }, [apps, selectedDepartmentId, searchQuery]);

  if (!authReady) return null;

  return (
    <div className="app">
      <Header />
      <main className="main-content">
        <div className="container">
          <div className="page-intro">
            <h2>Okta Apps</h2>
            <p>Browse Driscoll's R&D applications available through Okta single sign-on.</p>
          </div>

          <FilterBar
            departments={departments}
            selectedDepartmentId={selectedDepartmentId}
            searchQuery={searchQuery}
            onDepartmentChange={setSelectedDepartmentId}
            onSearchChange={setSearchQuery}
          />

          <AppGrid apps={filteredApps} loading={loading} error={error} />
        </div>
      </main>
      <footer className="footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} Driscolls. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AppMain;
